import { type ApiError } from '@resolute/schema';
import axios, { type AxiosError, type AxiosInstance } from 'axios';


import { clientEnv } from './env.js';
import { RequestError } from './request-error.js';

// Re-exported so existing importers of `RequestError` from this module keep
// working unchanged — only `query-client.ts` (eagerly loaded at app root)
// needed to switch to importing it directly from `./request-error.js`
// instead, to avoid pulling axios into the main bundle.
export { RequestError };

/** Shared Axios instance. The public site requires no authentication. */
export const apiClient: AxiosInstance = axios.create({
  baseURL: clientEnv.apiBaseUrl,
  timeout: 15_000,
  headers: { 'Content-Type': 'application/json' },
});

// Unwrap the API error envelope into a typed RequestError.
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiError>) => {
    const payload = error.response?.data?.error;
    return Promise.reject(
      new RequestError(
        payload?.code ?? 'NETWORK_ERROR',
        payload?.message ?? error.message ?? 'Request failed',
        error.response?.status,
        payload?.details,
      ),
    );
  },
);

/** Helper: GET and return the `data` field of the success envelope. */
export async function getData<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  const res = await apiClient.get<{ data: T }>(url, { params });
  return res.data.data;
}

/** Helper: GET returning both `data` and `meta`. */
export async function getDataWithMeta<T, M = Record<string, unknown>>(
  url: string,
  params?: Record<string, unknown>,
): Promise<{ data: T; meta: M }> {
  const res = await apiClient.get<{ data: T; meta: M }>(url, { params });
  return { data: res.data.data, meta: res.data.meta };
}
